import { z } from "zod";
import { defineStepConfig } from "../../registry/helpers";
import { VisibilityPredicateType } from "../../registry/visibility";

export const PLATFORM_HEARTBEAT_WORKFLOW_NAME = "Platform Heartbeat";

/**
 * Dependencies the heartbeat step can probe. Each name maps to an existing
 * backend health check — the handler never re-implements connectivity logic.
 *
 * `daytona` is the sandbox provider behind every code-sandbox step. Unlike the
 * first two it is optional per deployment, so the handler skips it where no
 * key is configured instead of reporting it down.
 */
export const PLATFORM_HEARTBEAT_PROBES = [
  "database",
  "rabbitmq",
  "daytona",
] as const;

export type PlatformHeartbeatProbe = (typeof PLATFORM_HEARTBEAT_PROBES)[number];

/**
 * One day of beats. Enough history to open the last few runs when an alert
 * fires, without letting the canary's run ledger grow without bound.
 */
export const DEFAULT_HEARTBEAT_RUN_RETENTION_HOURS = 24;

export const platformHeartbeatProbeResultSchema = z.object({
  probe: z.enum(PLATFORM_HEARTBEAT_PROBES),
  healthy: z.boolean(),
  latencyMs: z.number(),
  /** Present only when `healthy` is false. */
  error: z.string().optional(),
});

export type PlatformHeartbeatProbeResult = z.infer<
  typeof platformHeartbeatProbeResultSchema
>;

export const platformHeartbeatConfigSchema = z.object({
  probes: z
    .array(z.enum(PLATFORM_HEARTBEAT_PROBES))
    .prefault([...PLATFORM_HEARTBEAT_PROBES])
    .describe("Which critical dependencies to probe on each beat"),
  failOnProbeError: z
    .boolean()
    .prefault(false)
    .describe(
      "Fail the step when a probe reports unhealthy. Off by default: failing the step also suppresses the heartbeat pulse, which collapses two distinct signals (engine dead vs dependency dead) into one.",
    ),
  runRetentionHours: z
    .number()
    .int()
    .positive()
    .nullable()
    .prefault(DEFAULT_HEARTBEAT_RUN_RETENTION_HOURS)
    .describe(
      "At the end of each beat, delete this workflow's own runs older than this many hours. A canary running every few minutes would otherwise accrete run rows forever for no diagnostic gain. Null disables pruning.",
    ),
});

export type PlatformHeartbeatConfig = z.infer<
  typeof platformHeartbeatConfigSchema
>;

/**
 * Platform Heartbeat Step
 *
 * The canary at the end of a scheduled workflow: it does no business work, it
 * only proves the machinery is alive. Reaching this handler at all means the
 * whole chain held up — Temporal schedule fired, the worker polled, the DAG
 * resolved, and the step registry dispatched. On each beat it emits a liveness
 * pulse plus per-dependency probe results, so a monitor can alert on the
 * ABSENCE of the pulse (the one failure mode zero-valued counters cannot
 * distinguish from "idle").
 *
 * Deliberately does NOT fail on an unhealthy probe by default — see
 * `failOnProbeError`.
 */
export const platformHeartbeatStep = defineStepConfig({
  type: "platform_heartbeat",
  name: "Platform Heartbeat",
  description:
    "Emits a liveness pulse and probes critical dependencies. Alert on the absence of the pulse to detect a broken workflow engine.",
  category: "Debug",
  visibility: [[{ type: VisibilityPredicateType.Admin }]],

  inputSchema: z.object({
    scheduledTime: z
      .string()
      .optional()
      .describe(
        "ISO timestamp of the scheduled fire time, wired from the schedule trigger. Used to measure how late Temporal fired. Optional so a manual 'Run Now' test (which has no scheduled fire time) still executes.",
      ),
  }),

  outputSchema: z.object({
    heartbeatAt: z
      .string()
      .describe("ISO timestamp when this beat was recorded"),
    scheduleLagMs: z
      .number()
      .nullable()
      .describe(
        "Milliseconds between the scheduled fire time and this beat. Null when there is no scheduled time (manual run).",
      ),
    probeResults: z
      .array(platformHeartbeatProbeResultSchema)
      .describe("Per-dependency probe outcome"),
    allProbesHealthy: z
      .boolean()
      .describe("True when every configured probe reported healthy"),
    prunedRunCount: z
      .number()
      .describe(
        "How many of this workflow's own aged-out runs this beat deleted. 0 when pruning is disabled or nothing had aged out.",
      ),
  }),

  configSchema: platformHeartbeatConfigSchema,

  emits: [] as const,

  execution: {
    // Probes are bounded by their own timeouts; a beat should never linger.
    startToCloseTimeout: "1 minute",
    // One beat, one attempt. A retry would blur "this beat failed" into "this
    // beat eventually succeeded"; losing a single beat is harmless because the
    // absence alert spans several beats.
    retry: { maximumAttempts: 1 },
  },
});
